import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { USER_ALREADY_AUTHENTICATED, useAuth } from "../auth/AuthProvider.js";
import { BrandLogo } from "../ui/BrandLogo.js";

interface LocationState {
  from?: string;
}

export function SignIn() {
  const { signIn, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? "/portal";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // If a session already exists (e.g. user signed in on another tab and then
  // navigated here), skip the form entirely. While auth state is still being
  // restored, render nothing to avoid a flash of the form.
  if (status === "signed-in") {
    return <Navigate to={from} replace />;
  }
  if (status === "loading") {
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      // `signIn` already recovers from `UserAlreadyAuthenticated` for the
      // same/different-user cases, but keep a friendly fallback in case a
      // race surfaces it (e.g. another tab signed in mid-submit).
      if (err instanceof Error && err.name === USER_ALREADY_AUTHENTICATED) {
        navigate(from, { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto max-w-md px-6 py-10">
      <Link to="/" className="text-sm text-brand-700">
        &larr; Back
      </Link>
      <BrandLogo className="mx-auto mt-6" />
      <h1 className="mt-4 text-center text-2xl font-bold text-charcoal">Pro sign in</h1>

      <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
        <Field label="Email">
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            className={inputClass}
          />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className={inputClass + " pr-16"}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-3 my-auto h-8 px-2 text-sm font-medium text-brand-700"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </Field>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800 disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="mt-6 text-sm text-charcoal">
        New here?{" "}
        <Link to="/sign-up" className="font-semibold text-brand-700">
          Create your pro account
        </Link>
      </p>
    </main>
  );
}

export const inputClass =
  "block w-full min-h-[44px] rounded-xl border border-charcoal/30 bg-white px-4 text-base text-charcoal shadow-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600";

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-charcoal">{label}</span>
      {children}
    </label>
  );
}
